import { Fragment } from 'react';
import { SlideFrame } from '../SlideFrame';
import { PlanetDots } from '../PlanetDots';

// Pure companies funnel — no factors circle. The 1,100 → 10 transformation
// is what Slide 7 (Factor Model) explains via the 54 factors.
//
// Every number is real, currency-adjusted, sourced:
//   13,460 — Bloomberg INDX_MEMBERS across 16 country / regional indices
//            (Russell 3000, TOPIX, SH+SZ Composite, KOSPI, TAIEX, STOXX 600,
//            S&P/TSX, ASX 300, SET, HSI, JSE, Tadawul, Bovespa, BIST 100, IPC)
//    1,682 — FMP company-screener, NA exchanges only at $5B USD threshold
//            (NASDAQ+NYSE+AMEX in USD natively, TSX at 6.85B CAD ≈ 5B USD,
//            deduped against US cross-listings). $5B aligns with the Russell
//            1000 June 2024/2025 reconstitution market-cap floor — the same
//            admission threshold our screen uses, so the funnel is internally
//            consistent. Currency-correct: US exchange mktCap is always USD.
//    1,100 — Our screened universe: Russell 1000 + TSX top names + global ADRs
//       10 — Alpha Zone: top of composite, conviction-weighted
//
// The 1,100 → 1,682 gap is the deliberate expansion roadmap — ~600 names of
// European, Japanese, and other ADRs above $5B not yet in our screen.
export function Slide06ProcessFunnel({ universe }: { universe: number }) {
  const steps = [
    { label: 'LISTED',       big: '13,460', count: 5000,            color: '#7c8a9f', scale: 2.4,  hubRadius: 80,  dotRadius: 1.5, displaySize: 200, numberColor: 'text-white',     numberSize: 'text-[26px]', caption: 'Listed across the world’s major investable equity markets.' },
    { label: 'TRADABLE',     big: '1,682',  count: 1200,            color: '#e2e8f0', scale: 1.3,  hubRadius: 44,  dotRadius: 1.5, displaySize: 162, numberColor: 'text-white',     numberSize: 'text-[24px]', caption: 'Above $5B USD and accessible through North American brokerage. The Russell 1000 admission floor.' },
    { label: 'SCREENED',     big: universe.toLocaleString(), count: universe || 1100, color: '#e2e8f0', scale: 1.2, hubRadius: 40,  dotRadius: 1.4, displaySize: 138, numberColor: 'text-white',     numberSize: 'text-[22px]', caption: 'Russell 1000 + S&P/TSX top companies + global ADRs. The companies we score every day.' },
    { label: 'ALPHA ZONE',   big: '10',     count: 10,              color: '#c9a84c', scale: 5.5, hubRadius: 40,  dotRadius: 2.6, displaySize: 110, numberColor: 'text-[#c9a84c]', numberSize: 'text-[24px]', caption: 'The top 10 by composite score, conviction-weighted, rebalanced monthly.', ring: true },
  ];

  return (
    <SlideFrame pageNum={9} title="THE VISUALIZER  —  FINDING THE BEST"
      banner="Anyone can say we buy the best companies. We screen every one in the world to find them.">
      <div className="h-full flex flex-col justify-center pb-10">
        <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-x-2 items-start">
          {steps.map((s, i) => (
            <Fragment key={s.label}>
              <div className="flex flex-col items-center gap-2 px-1">
                <div className="text-[#c9a84c] text-[14px] font-bold tracking-widest mt-8 mb-[-32px]">{s.label}</div>
                <div className="relative flex items-center justify-center h-[320px] w-full overflow-visible">
                  <PlanetDots count={s.count} color={s.color} scale={s.scale} hubRadius={s.hubRadius} dotRadius={s.dotRadius} displaySize={s.displaySize} ring={s.ring} />
                  <div className={`absolute ${s.numberSize} ${s.numberColor} font-bold tabular-nums leading-none`}>{s.big}</div>
                </div>
                <p className="text-[#cbd5e1] text-[13px] leading-snug text-center max-w-[220px] mt-[-19px]">{s.caption}</p>
              </div>
              {i < steps.length - 1 && <div className="text-[#c9a84c] text-2xl font-light self-start mt-[180px]">→</div>}
            </Fragment>
          ))}
        </div>
      </div>
    </SlideFrame>
  );
}
